import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { HttpErrorNotifierService } from './http-error-notifier.service';
import { WebRoutes } from '@shared/constants/web-routes.enum';
import { AppRoutes } from '@shared/constants/routes.enum';
import { TRANSLATION_KEYS } from '@shared/constants/translation-keys.const';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { MidSessionTerminationService } from '@core/auth/mid-session-termination.service';

// Every mid-session type below only ever answers to a still-live session that must be torn down
// before the toast-only path would otherwise repeat itself on the next request.
const MID_SESSION_PROBLEM_TYPE_KEYS: Readonly<Record<string, string>> = {
  'https://vetcore.app/problems/account-banned': TRANSLATION_KEYS.loginAccountBanned,
  'https://vetcore.app/problems/organization-suspended': TRANSLATION_KEYS.loginOrganizationSuspended,
  'https://vetcore.app/problems/organization-billing-suspended':
    TRANSLATION_KEYS.loginOrganizationBillingSuspended,
  'https://vetcore.app/problems/staff-organization-access-required':
    TRANSLATION_KEYS.loginStaffOrganizationAccessRevoked,
};

// A 403 raised because the clinic's PLAN does not include the module, as opposed to one raised
// because the role's permission matrix denies it (`insufficient_permissions`). Both used to be the
// same bodiless 403 -- see the backend's `ModuleAuthorizationResultHandler` for why they were split.
// Reaching one is a bug in THIS app, not a user problem: every entry point into a module outside the
// plan is meant to be hidden (`hasModuleAccessGuard`, `*uvHasModule`), so the user must never be told
// they "lack permissions" for something they were never offered and cannot be granted.
const MODULE_NOT_IN_PLAN_PROBLEM_CODE = 'module_not_in_plan';

const SUBSCRIPTION_REQUIRED_PROBLEM_TYPE = 'https://vetcore.app/problems/subscription-required';
// Fallback match for backends that answer the 402 with the `code` extension but an unset or
// framework-default `type` (older ProblemDetails serializers rewrite unknown types to about:blank).
const SUBSCRIPTION_REQUIRED_PROBLEM_CODE = 'subscription_required';

export const errorNotificationInterceptor: HttpInterceptorFn = (request, next) => {
  const httpErrorNotifierService = inject(HttpErrorNotifierService);
  const router = inject(Router);
  const tokenService = inject(AuthTokenService);
  const midSessionTermination = inject(MidSessionTerminationService);

  return next(request).pipe(
    catchError((error: HttpErrorResponse | Error) => {
      if (error instanceof HttpErrorResponse && error.status === 402) {
        const problemType = error.error?.type as string | undefined;
        const problemCode = error.error?.code as string | undefined;

        if (
          problemType === SUBSCRIPTION_REQUIRED_PROBLEM_TYPE
          || problemCode === SUBSCRIPTION_REQUIRED_PROBLEM_CODE
        ) {
          // Never from inside the wizard: an organization that has not finished onboarding has no
          // subscription yet BY DESIGN -- step 5 is what buys one -- so a 402 here is a backend gate
          // that forgot the wizard (see SubscriptionAccessMiddleware), not a lapsed payment. Bouncing
          // the owner to "subscription required" made the last step unreachable, which is the one
          // screen that would have fixed it. Let the failing call surface as a normal store error.
          //
          // Nor from the recovery page, for the same reason one step further: the shell's root
          // singletons (AppointmentChangeRequestsStore, LocalUsersStore, DepartmentContextStore,
          // ClinicConversationsStore) keep polling every 60s and on `visibilitychange` once
          // constructed, so their 402s outlive the shell the owner just left and would each kick him
          // straight back off the one screen that can pay.
          if (
            !router.url.startsWith(WebRoutes.SubscriptionRequired)
            && !router.url.startsWith(WebRoutes.SubscriptionRecovery)
            && !router.url.startsWith(WebRoutes.Onboarding)
          ) {
            void router.navigateByUrl(WebRoutes.SubscriptionRequired);
          }
          return throwError(() => error);
        }
      }

      if (error instanceof HttpErrorResponse && error.status === 403) {
        const problemType = error.error?.type as string | undefined;

        if ((error.error?.code as string | undefined) === MODULE_NOT_IN_PLAN_PROBLEM_CODE) {
          if (isDevMode()) {
            // Loud for developers, silent for users: the fix belongs at whichever call site fired
            // this, not in a toast asking the user for permissions nobody can grant them.
            console.warn(
              '[module-not-in-plan] ' + request.method + ' ' + request.urlWithParams
              + ' needs the "' + String(error.error?.module) + '" module, which this plan does not'
              + ' include. Hide that entry point instead of firing the request.',
            );
          }
          return throwError(() => error);
        }

        const reasonKey = problemType ? MID_SESSION_PROBLEM_TYPE_KEYS[problemType] : undefined;
        if (reasonKey) {
          // Idempotency guard: several requests in flight when the ban/suspension lands can all
          // 403 with the same type, but once the tokens are gone (`AuthStore.endSession`, run off
          // this same request via `MidSessionTerminationService`) there is nothing left to tear
          // down -- the rest fall through to the generic toast path below, same as any other error.
          if (tokenService.tokens() !== null) {
            midSessionTermination.request(reasonKey);
            void router.navigateByUrl(AppRoutes.Login);
          }
          return throwError(() => error);
        }
      }

      httpErrorNotifierService.notify(request, error);
      return throwError(() => error);
    }),
  );
};
